import React, { useState, useEffect } from 'react';
import { Dropdown, Spinner, Modal, Button } from 'flowbite-react';
import { useToast } from 'src/context/ToastProvider';

import { BiDownload, BiTv, BiEdit } from 'react-icons/bi';

import { downloadCSV } from 'src/utils/downloadCsv';
import { useMutation, useQueryClient } from 'react-query';
import { createSlackChannelByPositionId, downloadData, getWorkspacesForEnvironment } from 'src/apis/position';
import { Drawer } from '../shared/Drawer';
import { useMediaQuery } from '@mui/material';
import BulkAssignmentChangeModal from './BulkAssignmentChangeModal';
import { ApplicationRoundType } from 'types';
import { useUserContext } from 'src/context/UserProvider';
import { get } from 'lodash';

interface Props {
	positionId?: string;
	hasSlackChannel?: boolean;
	existingSlackWorkspaceIds?: string[];
}

const RecruiterThreeDotMenu = ({ positionId, hasSlackChannel, existingSlackWorkspaceIds = [] }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [openBulkAssignmentModal, setOpenBulkAssignmentModal] = useState(false);
	const [drawerType, setDrawerType] = useState<string>('');
	const { isAccountManagerLoggedIn } = useUserContext();

	// Workspace selection state
	const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
	const [workspaces, setWorkspaces] = useState<Array<{ workspaceId: string, workspaceName: string, environment?: string }>>([]);
	const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
	const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
	const downloadDataMutation = useMutation(['downloadData'], downloadData, {
		onSuccess(data: any) {
			const csvData = data?.csvData;
			const downloadType = data?.downloadType;
			downloadCSV({
				data: csvData,
				columns:
					downloadType === 'download-assignment-submissions'
						? [
							'name',
							'email',
							'phone',
							'roundNumber',
							'studentCode',
							'assignmentLink',
							'assignmentDocumentLink',
							'assignmentSubmissionType',
							'assignmentLinkOptions',
							'assignmentFileOptions',
							'assignmentDeadline',
							'submissionLinks',
							'submissionFiles',
						]
						: downloadType === 'download-connected-users'
							? [
								'name',
								'email',
								'phone',
								'roundNumber',
								'status',
								'studentCode',
								'hasApplied',
								'isAdminPushed',
								'shownInterest',
							]
							: downloadType === 'download-recommended-users'
								? ['name', 'email', 'phone', 'studentCode', 'hasApplied', 'isAdminPushed', 'shownInterest']
								: [],
			});
			toast({
				title: 'Download Assignment Done!',
				type: 'success',
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured while shortlisting candidate ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const createSlackChannelByPositionIdFn = useMutation(
		['createSlackChannelByPositionId'],
		createSlackChannelByPositionId,
		{
			onSuccess(data: any) {
				queryClient.refetchQueries(['getPositionById', positionId]);
				toast({
					title: data?.message,
					type: 'success',
				});
				setShowWorkspaceModal(false);
				setSelectedWorkspaces([]);
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: `Error: ${errorMessage}`,
					type: 'error',
				});
			},
		},
	);

	// Fetch workspaces for current environment
	const fetchWorkspaces = async () => {
		setLoadingWorkspaces(true);
		try {
			const response = await getWorkspacesForEnvironment();
			// Filter out development workspaces - only show production workspaces
			const productionWorkspaces = (response.workspaces || []).filter(
				(workspace): workspace is typeof workspace & { environment: string } =>
					workspace.environment === 'production'
			);
			setWorkspaces(productionWorkspaces);
		} catch (error) {
			toast({
				title: 'Failed to fetch workspaces',
				type: 'error',
			});
		} finally {
			setLoadingWorkspaces(false);
		}
	};

	// Handle workspace selection
	const handleWorkspaceToggle = (workspaceId: string) => {
		if (existingSlackWorkspaceIds.includes(workspaceId)) {
			return;
		}
		setSelectedWorkspaces(prev =>
			prev.includes(workspaceId)
				? prev.filter(id => id !== workspaceId)
				: [...prev, workspaceId]
		);
	};

	// Handle create channel with selected workspaces
	const handleCreateSlackChannel = () => {
		if (selectedWorkspaces.length === 0) {
			toast({
				title: 'Please select at least one workspace',
				type: 'error',
			});
			return;
		}

		createSlackChannelByPositionIdFn.mutate({
			positionId: positionId || '',
			workspaceIds: selectedWorkspaces
		});
	};

	// Open workspace selection modal
	const handleCreateSlackChannelClick = () => {
		fetchWorkspaces();
		setShowWorkspaceModal(true);
	};

	const getDrawerContent = () => {
		if (drawerType === 'update-assignment-deadline') {
			return (
				<Drawer
					maxHeight="90dvh"
					modalMaxWidth="1000px"
					minHeight={'max-content'}
					backDropColor={isMobile ? 'rgba(0, 0, 0, 0.5)' : 'transparent'}
					boxShadow="0px -8px 32px 0px #00000040"
					isModal={!isMobile}
					isOpen={openBulkAssignmentModal}
					onClose={() => {
						setOpenBulkAssignmentModal(false);
					}}
					header={<h1 className="text-2xl font-bold pt-4 px-5">Edit Assignemnt</h1>}
					body={
						<div className="p-2">
							<BulkAssignmentChangeModal roundType={ApplicationRoundType.Assignment} />
						</div>
					}
				/>
			);
		} else if (drawerType === 'schedule-brief-debrief-call') {
			return (
				<Drawer
					maxHeight="90dvh"
					modalMaxWidth="1000px"
					minHeight={'max-content'}
					backDropColor={isMobile ? 'rgba(0, 0, 0, 0.5)' : 'transparent'}
					boxShadow="0px -8px 32px 0px #00000040"
					isModal={!isMobile}
					isOpen={openBulkAssignmentModal}
					onClose={() => {
						setOpenBulkAssignmentModal(false);
					}}
					header={<h1 className="text-2xl font-bold pt-4 px-5">Schedule Brief/Debrief Call</h1>}
					body={
						<div className="p-2">
							<BulkAssignmentChangeModal drawerType={drawerType} ctaText="Schedule Call" />
						</div>
					}
				/>
			);
		}
	};

	return (
		<>
			<div
				onClick={e => {
					e.stopPropagation();
				}}
				className="ml-[auto] [&_div[data-testid='flowbite-dropdown']]:!border-gray-200"
			>
				<Dropdown
					arrowIcon={false}
					color={'#C7C9D9'}
					className="dark:!bg-[#111E2C] !text-[#E4E4EB] rounded-[8px]"
					label=""
					dismissOnClick
					renderTrigger={() => (
						<img className="h-[24px] cursor-pointer" src="/assets/three-dots-vertical.svg" alt="menu" />
					)}
				>
					<Dropdown.Item
						onClick={() => {
							if (positionId) {
								downloadDataMutation.mutate({ positionId, downloadType: 'download-assignment-submissions' });
							}
						}}
						className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
					>
						{downloadDataMutation.isLoading ? (
							<Spinner size="sm" light={true} />
						) : (
							<>
								<BiDownload className="w-[16px] h-[16px]" />
								<p className="ml-[8px] text-[14px] font-[500] leading-[21px]">Download Submissions</p>
							</>
						)}
					</Dropdown.Item>
					<Dropdown.Item
						onClick={() => {
							if (positionId) {
								downloadDataMutation.mutate({ positionId, downloadType: 'download-connected-users' });
							}
						}}
						className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
					>
						{downloadDataMutation.isLoading ? (
							<Spinner size="sm" light={true} />
						) : (
							<>
								<BiDownload className="w-[16px] h-[16px]" />
								<p className="ml-[8px] text-[14px] font-[500] leading-[21px]">Download Applicants</p>
							</>
						)}
					</Dropdown.Item>
					<Dropdown.Item
						onClick={() => {
							if (positionId) {
								downloadDataMutation.mutate({ positionId, downloadType: 'download-recommended-users' });
							}
						}}
						className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
					>
						{downloadDataMutation.isLoading ? (
							<Spinner size="sm" light={true} />
						) : (
							<>
								<BiDownload className="w-[16px] h-[16px]" />
								<p className="ml-[8px] text-[14px] font-[500] leading-[21px] whitespace-pre">
									Download Recommendations
								</p>
							</>
						)}
					</Dropdown.Item>
					<Dropdown.Item
						onClick={() => {
							setOpenBulkAssignmentModal(true);
							setDrawerType('update-assignment-deadline');
						}}
						className={'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50]'}
					>
						{downloadDataMutation.isLoading ? (
							<Spinner size="sm" light={true} />
						) : (
							<>
								<BiEdit className="w-[16px] h-[16px]" />
								<p className="ml-[8px] text-[14px] font-[500] leading-[21px] whitespace-pre">Edit Assignment</p>
							</>
						)}
					</Dropdown.Item>
					<Dropdown.Item
						onClick={handleCreateSlackChannelClick}
						className={
							'dark:!text-[#C7C9D9] dark:hover:!bg-[#2c3c50] disabled:opacity-[0.5] disabled:pointer-events-none disabled:cursor-not-allowed'
						}
					>
						{createSlackChannelByPositionIdFn.isLoading ? (
							<Spinner size="sm" light={true} />
						) : (
							<>
								<BiTv className="w-[16px] h-[16px]" />
								<p className="ml-[8px] text-[14px] font-[500] leading-[21px] whitespace-pre">Create Slack Channel</p>
							</>
						)}
					</Dropdown.Item>
					{/* {isAccountManagerLoggedIn ? (
						<Dropdown.Item
							onClick={() => {
								setOpenBulkAssignmentModal(true);
								setDrawerType('schedule-brief-debrief-call');
							}}
							className={'dark:!text-[#616162] dark:hover:!bg-[#2c3c50]'}
						>
							{downloadDataMutation.isLoading ? (
								<Spinner size="sm" light={true} />
							) : (
								<>
									<BiPhoneCall className="w-[16px] h-[16px]" />
									<p className="ml-[8px] text-[14px] font-[500] leading-[21px] whitespace-pre">
										Schedule Brief/Debrief Call
									</p>
								</>
							)}
						</Dropdown.Item>
					) : null} */}
				</Dropdown>
			</div>
			{getDrawerContent()}

			{/* Workspace Selection Modal */}
			<Modal show={showWorkspaceModal} onClose={() => setShowWorkspaceModal(false)}>
				<Modal.Header>Select Workspaces for Slack Channel</Modal.Header>
				<Modal.Body>
					<div className="space-y-4">
						<p className="text-sm text-gray-600">
							Choose which workspaces you want to create Slack channels in.
							Shortlisted users will be added to channels in the selected workspaces.
						</p>

						{loadingWorkspaces ? (
							<div className="flex justify-center py-4">
								<Spinner size="md" />
							</div>
						) : (
							<div className="space-y-2 max-h-60 overflow-y-auto">
								{workspaces.map((workspace) => (
									<label
										key={workspace.workspaceId}
										className={`flex items-center space-x-3 ${existingSlackWorkspaceIds.includes(workspace.workspaceId) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
									>
										<input
											type="checkbox"
											checked={existingSlackWorkspaceIds.includes(workspace.workspaceId) || selectedWorkspaces.includes(workspace.workspaceId)}
											disabled={existingSlackWorkspaceIds.includes(workspace.workspaceId)}
											onChange={() => handleWorkspaceToggle(workspace.workspaceId)}
											className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
										/>
										<span className="text-sm font-medium text-gray-900">
											{workspace.workspaceName}
											{existingSlackWorkspaceIds.includes(workspace.workspaceId) ? ' (already created)' : ''}
										</span>
									</label>
								))}
							</div>
						)}

						{workspaces.length === 0 && !loadingWorkspaces && (
							<div className="text-sm text-gray-500 text-center py-4">
								<p>No workspaces found for current environment.</p>
								<p className="text-xs mt-2">Please create workspaces via admin panel first.</p>
							</div>
						)}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button
						color="gray"
						onClick={() => setShowWorkspaceModal(false)}
						disabled={createSlackChannelByPositionIdFn.isLoading}
					>
						Cancel
					</Button>
					<Button
						onClick={handleCreateSlackChannel}
						disabled={selectedWorkspaces.length === 0 || createSlackChannelByPositionIdFn.isLoading}
					>
						{createSlackChannelByPositionIdFn.isLoading ? (
							<>
								<Spinner size="sm" className="mr-2" />
								Creating...
							</>
						) : (
							`Create Channel${selectedWorkspaces.length > 1 ? 's' : ''} (${selectedWorkspaces.length})`
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default RecruiterThreeDotMenu;
